import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { recurrenceOptions } from './taskHelpers';

export const TaskModal = ({ open, onOpenChange, task, setTask, onSubmit, submitting, mode = 'add' }) => {
  const isEdit = mode === 'edit';
  const title = isEdit ? 'Edit Task' : 'Add New Task';
  const submitLabel = isEdit ? (submitting ? 'Saving...' : 'Save Changes') : (submitting ? 'Adding...' : 'Add Task');
  const prefix = isEdit ? 'edit-task' : 'task';

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="modal-content sm:max-w-md mx-auto p-0 overflow-hidden max-h-[90vh] flex flex-col">
        <DialogHeader className="px-4 pt-4 pb-2 sm:px-6 sm:pt-6 flex-shrink-0">
          <DialogTitle className="headline-md">{title}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="px-4 sm:px-6 pb-4 space-y-3 overflow-y-auto flex-1">
            {/* Title */}
            <div>
              <label className="label-md block mb-1">Title *</label>
              <input
                type="text"
                value={task.title}
                onChange={(e) => setTask({ ...task, title: e.target.value })}
                className="input-field"
                placeholder="e.g., Electricity Bill"
                required
                data-testid={`${prefix}-title-input`}
              />
            </div>

            {/* Category + Amount row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-md block mb-1">Category</label>
                <select
                  value={task.category}
                  onChange={(e) => setTask({ ...task, category: e.target.value })}
                  className="input-field"
                  data-testid={`${prefix}-category-select`}
                >
                  <option value="bill">Bill</option>
                  <option value="subscription">Subscription</option>
                  <option value="appointment">Appointment</option>
                  <option value="task">Task</option>
                  <option value="general">General</option>
                </select>
              </div>
              <div>
                <label className="label-md block mb-1">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={task.amount}
                  onChange={(e) => setTask({ ...task, amount: e.target.value })}
                  className="input-field"
                  placeholder="0.00"
                  data-testid={`${prefix}-amount-input`}
                />
              </div>
            </div>

            {/* Currency (edit mode) */}
            {isEdit && (
              <div>
                <label className="label-md block mb-1">Currency</label>
                <select
                  value={task.currency || 'USD'}
                  onChange={(e) => setTask({ ...task, currency: e.target.value })}
                  className="input-field"
                  data-testid="edit-task-currency-select"
                >
                  <option value="USD">USD ($)</option>
                  <option value="AUD">AUD (A$)</option>
                  <option value="SGD">SGD (S$)</option>
                  <option value="INR">INR (&#8377;)</option>
                  <option value="EUR">EUR (&euro;)</option>
                  <option value="GBP">GBP (&pound;)</option>
                  <option value="NZD">NZD (NZ$)</option>
                  <option value="CAD">CAD (C$)</option>
                </select>
              </div>
            )}

            {/* Due Date + Time row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-md block mb-1">Due Date</label>
                <input
                  type="date"
                  value={task.due_date ? task.due_date.split('T')[0] : ''}
                  onChange={(e) => {
                    const time = task.due_time || '';
                    setTask({ ...task, due_date: e.target.value, due_time: time });
                  }}
                  className="input-field"
                  data-testid={`${prefix}-due-date-input`}
                />
              </div>
              <div>
                <label className="label-md block mb-1">Time</label>
                <input
                  type="time"
                  value={task.due_time || ''}
                  onChange={(e) => setTask({ ...task, due_time: e.target.value })}
                  className="input-field"
                  data-testid={`${prefix}-due-time-input`}
                />
              </div>
            </div>

            {/* Provider */}
            <div>
              <label className="label-md block mb-1">Provider/Service</label>
              <input
                type="text"
                value={task.provider}
                onChange={(e) => setTask({ ...task, provider: e.target.value })}
                className="input-field"
                placeholder="e.g., PG&E, Netflix, Dr. Smith"
                data-testid={`${prefix}-provider-input`}
              />
            </div>

            {/* Recurrence + Priority row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-md block mb-1">Recurrence</label>
                <select
                  value={task.recurrence}
                  onChange={(e) => setTask({ ...task, recurrence: e.target.value })}
                  className="input-field"
                  data-testid={`${prefix}-recurrence-select`}
                >
                  {recurrenceOptions.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-md block mb-1">Priority: {task.priority}</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={task.priority}
                  onChange={(e) => setTask({ ...task, priority: e.target.value })}
                  className="w-full accent-[#005bc1] mt-2"
                  data-testid={`${prefix}-priority-slider`}
                />
              </div>
            </div>

            {/* Description (add mode only) */}
            {!isEdit && (
              <div>
                <label className="label-md block mb-1">Description</label>
                <textarea
                  value={task.description}
                  onChange={(e) => setTask({ ...task, description: e.target.value })}
                  className="input-field min-h-[60px] resize-none"
                  placeholder="Optional notes..."
                  rows={2}
                  data-testid="task-description-input"
                />
              </div>
            )}
          </div>

          {/* Sticky footer buttons */}
          <div className="flex gap-3 px-4 py-3 sm:px-6 border-t border-[#f2f4f6] bg-white flex-shrink-0">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary flex-1 disabled:opacity-50"
              data-testid={isEdit ? 'save-edit-task-btn' : 'submit-task-btn'}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
